// Функція для обчислення математичного виразу з підтримкою дужок
export const evaluateMathExpression = (expression: string): { result: number | null; isExpression: boolean } => {
  // Видалити пробіли
  const cleanExpr = expression.replace(/\s/g, '');
  
  // Перевірити чи це просто число
  if (/^-?\d+\.?\d*$/.test(cleanExpr)) {
    return { result: null, isExpression: false };
  }
  
  // Перевірити чи є математичні оператори або дужки
  if (!/[+\-*/()]/.test(cleanExpr)) {
    return { result: null, isExpression: false };
  }
  
  // Перевірити чи вираз валідний (тільки цифри, точки, оператори та дужки)
  if (!/^[\d.+\-*/()]+$/.test(cleanExpr)) {
    return { result: null, isExpression: false };
  }
  
  // Перевірити баланс дужок
  let parenCount = 0;
  for (const char of cleanExpr) {
    if (char === '(') parenCount++;
    if (char === ')') parenCount--;
    if (parenCount < 0) return { result: null, isExpression: true };
  }
  if (parenCount !== 0) {
    return { result: null, isExpression: true };
  }
  
  // Перевірити чи вираз не закінчується оператором
  if (/[+\-*/(]$/.test(cleanExpr)) {
    return { result: null, isExpression: true };
  }
  
  try {
    // Токенізація з підтримкою від'ємних чисел
    const tokens: string[] = [];
    let i = 0;
    
    while (i < cleanExpr.length) {
      const char = cleanExpr[i];
      
      if (char >= '0' && char <= '9' || char === '.') {
        // Збираємо число
        let num = '';
        while (i < cleanExpr.length && (cleanExpr[i] >= '0' && cleanExpr[i] <= '9' || cleanExpr[i] === '.')) {
          num += cleanExpr[i];
          i++;
        }
        tokens.push(num);
      } else if (char === '-') {
        // Перевіряємо чи це унарний мінус
        const prevToken = tokens[tokens.length - 1];
        const isUnary = tokens.length === 0 || 
                        prevToken === '(' || 
                        prevToken === '+' || 
                        prevToken === '-' || 
                        prevToken === '*' || 
                        prevToken === '/';
        
        if (isUnary) {
          // Унарний мінус - збираємо від'ємне число
          let num = '-';
          i++;
          
          // Перевіряємо чи після мінуса йде дужка
          if (i < cleanExpr.length && cleanExpr[i] === '(') {
            // Унарний мінус перед дужкою: замінюємо на (0-1)*
            tokens.push('(');
            tokens.push('0');
            tokens.push('-');
            tokens.push('1');
            tokens.push(')');
            tokens.push('*');
            continue;
          }
          
          while (i < cleanExpr.length && (cleanExpr[i] >= '0' && cleanExpr[i] <= '9' || cleanExpr[i] === '.')) {
            num += cleanExpr[i];
            i++;
          }
          if (num === '-') {
            return { result: null, isExpression: true };
          }
          tokens.push(num);
        } else {
          tokens.push(char);
          i++;
        }
      } else if (['+', '*', '/', '(', ')'].includes(char)) {
        tokens.push(char);
        i++;
      } else {
        return { result: null, isExpression: true };
      }
    }
    
    if (tokens.length < 2) {
      return { result: null, isExpression: true };
    }
    
    // Shunting-yard algorithm
    const outputQueue: (number | string)[] = [];
    const operatorStack: string[] = [];
    
    const precedence: { [key: string]: number } = {
      '+': 1,
      '-': 1,
      '*': 2,
      '/': 2
    };
    
    const isOperator = (token: string) => ['+', '-', '*', '/'].includes(token);
    
    for (const token of tokens) {
      if (/^-?\d+\.?\d*$/.test(token)) {
        // Число (може бути від'ємним)
        outputQueue.push(parseFloat(token));
      } else if (token === '(') {
        operatorStack.push(token);
      } else if (token === ')') {
        while (operatorStack.length > 0 && operatorStack[operatorStack.length - 1] !== '(') {
          outputQueue.push(operatorStack.pop()!);
        }
        if (operatorStack.length === 0) {
          return { result: null, isExpression: true };
        }
        operatorStack.pop();
      } else if (isOperator(token)) {
        while (
          operatorStack.length > 0 &&
          operatorStack[operatorStack.length - 1] !== '(' &&
          isOperator(operatorStack[operatorStack.length - 1]) &&
          precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]
        ) {
          outputQueue.push(operatorStack.pop()!);
        }
        operatorStack.push(token);
      }
    }
    
    while (operatorStack.length > 0) {
      const op = operatorStack.pop()!;
      if (op === '(' || op === ')') {
        return { result: null, isExpression: true };
      }
      outputQueue.push(op);
    }
    
    // Обчислюємо постфіксний вираз
    const evalStack: number[] = [];
    
    for (const token of outputQueue) {
      if (typeof token === 'number') {
        evalStack.push(token);
      } else if (isOperator(token)) {
        if (evalStack.length < 2) {
          return { result: null, isExpression: true };
        }
        const b = evalStack.pop()!;
        const a = evalStack.pop()!;
        
        switch (token) {
          case '+': evalStack.push(a + b); break;
          case '-': evalStack.push(a - b); break;
          case '*': evalStack.push(a * b); break;
          case '/': evalStack.push(a / b); break;
        }
      }
    }
    
    if (evalStack.length === 1 && isFinite(evalStack[0])) {
      const result = Math.round(evalStack[0] * 100) / 100;
      return { result, isExpression: true };
    }
    
    return { result: null, isExpression: true };
  } catch {
    return { result: null, isExpression: true };
  }
};
